import { _ } from "extensions/underscore";
import { Highcharts } from "extensions/highcharts";
import moment from "moment";

import strings from "common/strings";

import {
    SET_GLOBAL_DATE_RANGE_ONLY
} from "common/actions/common";

import { references } from "common/helpers/references";
import { pazienHistory } from "common/components/pazienHistory";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import * as keyToFunc from "./functionReferences";

const date_unix = function(point) {
    return moment.utc(point["date_time"]).valueOf();
};
const propGetter = function(prop, opts) {
    let val;
    if (opts.by === "count") {
        val = {
            value: `${prop}_count`,
            type: "number"
        };
    } else if (opts.by === "amount") {
        val = {
            value: `${prop}_amount`,
            type: "amount"
        };
    }
    return val;
};
const keyToFunction = function(key, opts) {
    // If there is a function on the given key then use it instead the key
    if (keyToFunc[key]) {
        return keyToFunc[key](opts);
    }
    return key;
};

export const DefaultTypeConfig = function(opts, instanceProps) {
    // Normalize opts
    opts.chart = opts.chart || {};
    
    this.urlRoot = _.result(instanceProps, "urlRoot");
    
    this.api_endpoint = instanceProps.api_endpoint;
    
    // Total values computed for charts and legends
    this.totals = _.defaults({}, instanceProps.totals, {});
    
    // Widget types
    this.quick = _.defaults({}, instanceProps.quick, {});
    this.table = _.defaults({}, instanceProps.table, {});
    this.pie = _.defaults({}, instanceProps.pie, {});
    
    this.map = {
        data: {
            defaultMetric: opts.by
        },
        get extend() {
            const defaultMapChartOptions = {
                colorAxis: {}
            };
            
            // eslint-disable-next-line eqeqeq
            if (opts.chart.minValue != null) {
                defaultMapChartOptions.colorAxis.min = opts.chart.minValue;
            }
            
            // eslint-disable-next-line eqeqeq
            if (opts.chart.tooltipData != null) {
                // Mutate tooltipData here as it has keyToFunc here in a closure. Ahhhhh, I hate mutations so much!
                opts.chart.tooltipData = _.map(opts.chart.tooltipData, (val) => {
                    const v = _.clone(val);
                    v.key = keyToFunction(v.key, _.pick(opts, "by"));
                    return v;
                });
            }
            
            return defaultMapChartOptions;
        },
        
        get series() {
            return _.map(opts.chart.series, (s, idx) => {
                const key = s.key;
                const extraDataPoints = {};
                
                return {
                    name: s.name,
                    valueType: s.valueType,
                    pointFormat: {
                        issuing_country: "issuing_country",
                        value: keyToFunction(key, _.pick(opts, "by")),
                        ...extraDataPoints
                    },
                    object: true,
                    extend: {
                        joinBy: s.joinBy,
                        states: {
                            hover: {
                                color: "#579EFF"
                            }
                        }
                    }
                };
            });
        }
    };
    
    this.doublePivot = _.deepExtend({
        showBadgeInTabNavi: true,
        summary: {
            enabled: false
        }
    }, instanceProps.doublePivot);
    
    this.trendline = _.deepExtend({
        chart: {
            get extend() {
                const defaultTrendlineChartOptions = {};
                
                if (opts.showSummary) {
                    defaultTrendlineChartOptions.legend = {
                        enabled: false
                    };
                }
                
                // Check if widget has custom setup for yAxises
                if (opts.chart.yAxis && opts.chart.yAxis.length) {
                    defaultTrendlineChartOptions.yAxis = _.clone(opts.chart.yAxis);
                }
                
                return defaultTrendlineChartOptions;
            },
            
            get series() {
                return _.map(opts.chart.series, (s, idx) => {
                    const sClone = _.pick(s, "name", "type", "valueType", "color", "dashStyle", "extend");
                    const key = s.key;
                    const color = sClone.color || (idx === 0 ? opts.color : Highcharts.getOptions().colors[idx]);
                    
                    if (!sClone.name) {
                        throw new Error("Name is required");
                    } else if (!sClone.type) {
                        throw new Error("Type is required");
                    }
                    
                    return _.deepExtend(sClone, {
                        pointFormat: [date_unix, keyToFunction(key, opts)],
                        extend: {
                            yAxis: s.yAxis || 0,
                            fillColor: Highcharts.pazUtils.gradient(color),
                            color,
                            dashStyle: sClone.dashStyle || "solid"
                        }
                    });
                });
            }
        }
    }, instanceProps.trendline);
    
    this.drilldown = function(widgetType, actor, x) {
        const urlRoot = _.result(instanceProps, "urlRoot");

        const pivot = opts.pivot;
        let after = _.noop;

        const qualifiers = new Qualifiers();
        const display_options = new Qualifiers({
            value: "card:all"
        });
        
        if (widgetType === "trendline" && !_.isUndefined(x)) {
            const pointUTC = moment.utc(Number.parseInt(x, 10));
            
            after = () => {
                SET_GLOBAL_DATE_RANGE_ONLY({
                    startDate: pointUTC,
                    endDate: pointUTC
                });
            };
        }

        if (pivot && actor) {
            // We have both pivot and actor
            if (actor === strings.other) {
                // Handle "other" as a label to just send a user to card directly
                display_options.replace(`card:${pivot}`);
            } else {
                qualifiers.add(`${pivot}:${Qualifiers.normalizeValue(actor)}`);
            }
        } else if (pivot) {
            // We only have pivot, but no actor
            display_options.replace(`card:${pivot}`);
        }

        // Build a new fragment now with search and display_options
        const fragment = `${urlRoot}/${display_options.toString(true)}/${qualifiers.toString(true)}`;

        pazienHistory.navigateWithParams(fragment, {
            trigger: true
        });

        after();
    };
};

// Expose some functions here to simplify children
DefaultTypeConfig.date_unix = date_unix;
DefaultTypeConfig.PRCNT = "<small>%</small>";
DefaultTypeConfig.references = references;
DefaultTypeConfig.keyToFunc = keyToFunc;
DefaultTypeConfig.propGetter = propGetter;
